import React, { useState, useEffect } from 'react';
import { Target, BarChart3, MapPin, FileText, TrendingUp, Share2, Megaphone, Building, Grid3X3 } from 'lucide-react';
import Navbar from '../Common/Navbar';

const PricingPageComponent = () => {
  const [billingCycle, setBillingCycle] = useState('annually');
  const [activeToolkit, setActiveToolkit] = useState('seo');
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toolkitData = {
    'seo': {
      title: 'SEO',
      icon: <Target className="text-info" size={24} />,
      active: true
    },
    'ai-seo': {
      title: 'AI SEO',
      icon: <TrendingUp className="text-info" size={24} />,
      active: false
    },
    'traffic': {
      title: 'Traffic & Market',
      icon: <BarChart3 className="text-primary" size={24} />,
      active: false
    },
    'local': {
      title: 'Local',
      icon: <MapPin className="text-warning" size={24} />,
      active: false
    },
    'content': {
      title: 'Content',
      icon: <FileText className="text-success" size={24} />,
      active: false
    },
    'social': {
      title: 'Social',
      icon: <Share2 className="text-primary" size={24} />,
      active: false
    },
    'advertising': {
      title: 'Advertising',
      icon: <Megaphone className="text-danger" size={24} />,
      active: false
    },
    'ai-pr': {
      title: 'AI PR',
      icon: <Megaphone className="text-info" size={24} />,
      active: false
    }
  };

  const pricingPlans = {
    seo: {
      pro: {
        name: "Pro",
        description: "For beginners and individual projects",
        monthly: { price: 139, originalPrice: 139 },
        annually: { price: 117.33, originalPrice: 139 },
        features: [
          "Up to 5 projects",
          "Keyword research tools",
          "Competitor analysis tools",
          "Position Tracking",
          "Backlink Analysis",
          "Site Audit"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      guru: {
        name: "Guru",
        description: "For small businesses",
        monthly: { price: 249, originalPrice: 249 },
        annually: { price: 208.33, originalPrice: 249 },
        features: [
          "All Pro features plus:",
          "Up to 15 projects",
          "Historical data",
          "Multi-location and device data",
          "ChatGPT and AI Mode tracking",
          "Content marketing tools"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      business: {
        name: "Business",
        description: "For agencies and mid-market companies",
        monthly: { price: 499, originalPrice: 499 },
        annually: { price: 416.66, originalPrice: 499 },
        features: [
          "All Guru features plus:",
          "Up to 40 projects",
          "Share of Voice",
          "Extended limits",
          "API access",
          "Migration from third-party tools"
        ],
        cta: "Subscribe",
        ctaSecondary: "or try custom plan"
      }
    },
    'ai-seo': {
      base: {
        name: "Base",
        description: "For businesses that want to track how they're mentioned in AI search",
        monthly: { price: 99, originalPrice: 99, perDomain: true },
        annually: { price: 99, originalPrice: 99, perDomain: true },
        features: [
          "AI visibility reports for any domain",
          "Mentions from ChatGPT, Google AI, Gemini, and Perplexity",
          "Prompt tracking and competitive benchmarking",
          "Site audit for AI crawlability",
          "Weekly data updates"
        ],
        cta: "Subscribe",
        ctaSecondary: "or try custom plan"
      }
    },
    content: {
      base: {
        name: "Base",
        description: "For individuals and small teams scaling content production",
        monthly: { price: 60, originalPrice: 60 },
        annually: { price: 50, originalPrice: 60 },
        features: [
          "Data-driven content ideas",
          "10,000 articles/mo",
          "5 SEO content boosts/mo",
          "SEO content briefs",
          "Content in your brand voice"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      }
    },
    advertising: {
      base: {
        name: "Base",
        description: "For small businesses and marketers scaling ads and maximizing ROI",
        monthly: { price: 99, originalPrice: 99 },
        annually: { price: 82.5, originalPrice: 99 },
        features: [
          "Unlimited ad spend",
          "Unified dashboard for Google and Meta",
          "Unlimited ad creative generation",
          "Competitor keyword analysis",
          "Google Shopping ads research",
          "Unlimited Google Ads exports"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      pro: {
        name: "Pro",
        description: "For advanced advertisers seeking in-depth competitor insights",
        monthly: { price: 220, originalPrice: 220 },
        annually: { price: 183.33, originalPrice: 220 },
        features: [
          "All Base features plus:",
          "AdClarity",
          "All competitor ads",
          "Display, video, or social ads",
          "Competitor landing page views"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      }
    },
    social: {
      base: {
        name: "Base",
        description: "For individuals and small teams",
        monthly: { price: 20, originalPrice: 20 },
        annually: { price: 16.67, originalPrice: 20 },
        features: [
          "All essential tools for SMM",
          "Up to 5 profiles to manage",
          "Up to 10 profiles to track",
          "30 monthly content generations",
          "50 monthly posts to publish"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      pro: {
        name: "Pro",
        description: "For social media pros, businesses and agencies",
        monthly: { price: 40, originalPrice: 40 },
        annually: { price: 33.33, originalPrice: 40 },
        features: [
          "All Base features plus:",
          "Up to 10 profiles to manage",
          "Up to 20 profiles to track",
          "Unlimited content generations",
          "Unlimited posts to publish"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      business: {
        name: "Business",
        description: "For scaling businesses, agencies, and brand marketing teams",
        monthly: { price: 250, originalPrice: 250 },
        annually: { price: 208.33, originalPrice: 250 },
        features: [
          "All Pro features plus:",
          "Influencer Analytics tool",
          "Media Monitoring tool"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      }
    },
    local: {
      base: {
        name: "Base",
        description: "For small local businesses",
        monthly: { price: 30, originalPrice: 30 },
        annually: { price: 25, originalPrice: 30 },
        features: [
          "Google Business Profile optimization",
          "GBP review management with AI",
          "Map Rank Tracker: 375 credits"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      pro: {
        name: "Pro",
        description: "For growing businesses",
        monthly: { price: 60, originalPrice: 60 },
        annually: { price: 50, originalPrice: 60 },
        features: [
          "All Base features plus:",
          "Listing Management",
          "Review management and generation",
          "API access",
          "Map Rank Tracker: 1,225 credits"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      business: {
        name: "Business",
        description: "For large businesses",
        monthly: { price: 0, originalPrice: 0, custom: true },
        annually: { price: 0, originalPrice: 0, custom: true },
        features: [
          "All Pro features plus:",
          "Custom pricing and billing cycle",
          "Customizable limits",
          "Priority customer support"
        ],
        cta: "Contact sales",
        ctaSecondary: "Let's talk"
      }
    },
    'ai-pr': {
      base: {
        name: "Base",
        description: "For individuals focused on PR essentials",
        monthly: { price: 149, originalPrice: 149 },
        annually: { price: 124.17, originalPrice: 149 },
        features: [
          "AI-cited media finder",
          "Global media database with AI search",
          "AI-driven journalist recommendations",
          "Media outreach tool",
          "AI for email and press release writing",
          "Email analytics"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      pro: {
        name: "Pro",
        description: "For those who grow and track their PR impact",
        monthly: { price: 279, originalPrice: 279 },
        annually: { price: 232.5, originalPrice: 279 },
        features: [
          "All Base features plus:",
          "Online media monitoring with AI support",
          "Audience and traffic insights for media outlets",
          "Exporting contacts",
          "Scheduling follow-up emails",
          "Advanced email analytics",
          "Tracking which pitches led to earned media coverage"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      }
    },
    traffic: {
      pro: {
        name: "Pro",
        description: "For those making strategic data-driven decisions",
        monthly: { price: 289, originalPrice: 289 },
        annually: { price: 240.83, originalPrice: 289 },
        features: [
          "Traffic Overview",
          "Daily Trends",
          "Market & Industry Overview",
          "Traffic distribution (incl. AI Traffic)",
          "Competitor monitoring tools",
          "Regional trends tools"
        ],
        cta: "Try for free",
        ctaSecondary: "or subscribe"
      },
      business: {
        name: "Business",
        description: "For teams managing growth and complexity",
        monthly: { price: 0, originalPrice: 0, comingSoon: true },
        annually: { price: 0, originalPrice: 0, comingSoon: true },
        features: [
          "All Pro features plus:",
          "Market & Industry Overview",
          "Page Groups",
          "Advanced notification center",
          "Custom dashboards",
          "Custom competitor monitoring"
        ],
        cta: "Coming soon",
        ctaSecondary: ""
      }
    }
  };

  const getCurrentPrice = (plan) => {
    return billingCycle === 'annually' ? plan.annually : plan.monthly;
  };

  const getSavings = () => {
    if (billingCycle !== 'annually') return '';
    
    const savingsMap = {
      'seo': 'save up to 17%',
      'content': 'save up to 17%',
      'advertising': 'save up to 25%',
      'social': 'save up to 17%',
      'local': 'save up to 17%',
      'ai-pr': 'save up to 17%',
      'traffic': 'save up to 17%'
    };
    
    return savingsMap[activeToolkit] || '';
  };

  const getCurrentPricingPlans = () => {
    return pricingPlans[activeToolkit] || pricingPlans.seo;
  };

  const getPageTitle = () => {
    const titles = {
      'seo': 'SEO Plans',
      'ai-seo': 'AI SEO Plans',
      'content': 'Content Plans',
      'advertising': 'Advertising Plans',
      'social': 'Social Plans',
      'local': 'Local Plans',
      'ai-pr': 'AI PR Plans',
      'traffic': 'Traffic & Market Plans'
    };
    return titles[activeToolkit] || 'SEO Plans';
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      
      {/* Add Navbar Component Here */}
      <Navbar />

      {/* Main Content - Three Column Layout */}
      <div style={{ display: 'flex', gap: '2rem', padding: windowWidth <= 768 ? '1rem' : '2rem', maxWidth: '1400px', margin: '0 auto', alignItems: 'flex-start', flexDirection: windowWidth <= 992 ? 'column' : 'row', marginTop: '80px' }}>
        
        {/* Left Sidebar - Toolkits */}
        <div style={{ width: windowWidth <= 992 ? '100%' : '240px', backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', position: windowWidth <= 992 ? 'static' : 'sticky', top: '100px', flexShrink: 0 }}>
          <h6 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1F2937', marginBottom: '1.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Toolkits</h6>
          
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {Object.entries(toolkitData).map(([key, toolkit]) => (
              <li 
                key={key}
                onClick={() => setActiveToolkit(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.75rem',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  backgroundColor: activeToolkit === key ? '#FEF3C7' : 'transparent',
                  marginBottom: '0.25rem',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ marginRight: '0.75rem', display: 'flex', alignItems: 'center' }}>
                  {toolkit.icon}
                </div>
                <span style={{ color: activeToolkit === key ? '#1F2937' : '#6B7280', fontSize: '0.9rem', fontWeight: activeToolkit === key ? '600' : '500' }}>
                  {toolkit.title}
                </span>
              </li>
            ))}
          </ul>
          
          <div style={{ height: '1px', backgroundColor: '#E5E7EB', margin: '1.5rem 0' }}></div>
          
          <a href="#" style={{ display: 'flex', alignItems: 'center', padding: '0.75rem', color: '#6B7280', textDecoration: 'none', fontSize: '0.9rem', borderRadius: '8px' }}>
            <Building size={18} style={{ marginRight: '0.75rem' }} />
            Enterprise
          </a>
          
          <a href="#" style={{ display: 'flex', alignItems: 'center', padding: '0.75rem', color: '#6B7280', textDecoration: 'none', fontSize: '0.9rem', borderRadius: '8px' }}>
            <Grid3X3 size={18} style={{ marginRight: '0.75rem' }} />
            App center
          </a>
        </div>

        {/* Center Content Area - Pricing Cards */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Header with Toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: windowWidth <= 768 ? 'flex-start' : 'center', marginBottom: '2rem', flexDirection: windowWidth <= 768 ? 'column' : 'row', gap: windowWidth <= 768 ? '1rem' : '0' }}>
            <h1 style={{ fontSize: windowWidth <= 768 ? '1.5rem' : '2rem', fontWeight: '700', color: '#1F2937', margin: 0 }}>{getPageTitle()}</h1>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span 
                onClick={() => setBillingCycle('monthly')}
                style={{ 
                  fontWeight: '500', 
                  color: billingCycle === 'monthly' ? '#1F2937' : '#6B7280', 
                  cursor: 'pointer' 
                }}
              >
                Monthly
              </span>
              
              <div 
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annually' : 'monthly')}
                style={{
                  position: 'relative',
                  width: '60px',
                  height: '30px',
                  backgroundColor: billingCycle === 'annually' ? '#F97316' : '#E5E7EB',
                  borderRadius: '15px',
                  cursor: 'pointer',
                  transition: 'background 0.3s'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '3px',
                  left: billingCycle === 'annually' ? '33px' : '3px',
                  width: '24px',
                  height: '24px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  transition: 'left 0.3s',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}></div>
              </div>
              
              <span 
                onClick={() => setBillingCycle('annually')}
                style={{ 
                  fontWeight: '500', 
                  color: billingCycle === 'annually' ? '#1F2937' : '#6B7280', 
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}
              >
                Annually
                {getSavings() && (
                  <span style={{
                    background: '#F97316',
                    color: 'white',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>{getSavings()}</span>
                )}
              </span>
            </div>
          </div>

          {/* Pricing Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: windowWidth <= 768 ? '1fr' : (Object.keys(getCurrentPricingPlans()).length === 1 ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))'),
            gap: '1.5rem',
            maxWidth: Object.keys(getCurrentPricingPlans()).length === 1 ? '400px' : 'none'
          }}>
            {Object.entries(getCurrentPricingPlans()).map(([key, plan]) => {
              const currentPrice = getCurrentPrice(plan);
              const isFeatured = activeToolkit === 'seo' && key === 'guru';
              const isCustom = currentPrice.custom;
              const isComingSoon = currentPrice.comingSoon;
              
              return (
                <div 
                  key={key} 
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: windowWidth <= 768 ? '1.5rem' : '2rem',
                    boxShadow: isFeatured ? '0 8px 30px rgba(249, 115, 22, 0.2)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: isFeatured ? '2px solid #F97316' : '1px solid #E5E7EB',
                    transition: 'all 0.3s',
                    opacity: isComingSoon ? 0.8 : 1,
                    transform: (isFeatured && windowWidth > 768) ? 'scale(1.05)' : 'scale(1)'
                  }}
                >
                  <h3 style={{ fontSize: windowWidth <= 768 ? '1.25rem' : '1.5rem', fontWeight: '700', color: '#1F2937', marginBottom: '0.5rem' }}>{plan.name}</h3>
                  <p style={{ color: '#6B7280', fontSize: '0.95rem', marginBottom: '1.5rem' }}>{plan.description}</p>
                  
                  {!isCustom && !isComingSoon && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: windowWidth <= 768 ? '2rem' : '2.5rem', fontWeight: '700', color: '#1F2937' }}>${currentPrice.price}</span>
                        <span style={{ color: '#6B7280', fontSize: '1rem' }}>/mo{currentPrice.perDomain ? ' per domain' : ''}</span>
                        {billingCycle === 'annually' && currentPrice.originalPrice !== currentPrice.price && (
                          <span style={{ textDecoration: 'line-through', color: '#9CA3AF', fontSize: windowWidth <= 768 ? '1rem' : '1.2rem' }}>${currentPrice.originalPrice}</span>
                        )}
                      </div>
                      
                      <p style={{ color: '#6B7280', fontSize: '0.9rem', marginBottom: '2rem' }}>
                        {billingCycle === 'annually' ? 'billed annually' : 'billed monthly'}
                      </p>
                    </>
                  )}
                  
                  {isComingSoon && (
                    <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                      <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#6B7280' }}>Coming soon</span>
                    </div>
                  )}
                  
                  <button 
                    disabled={isComingSoon}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1.5rem',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '1rem',
                      border: 'none',
                      cursor: isComingSoon ? 'not-allowed' : 'pointer',
                      marginBottom: '0.75rem',
                      backgroundColor: isComingSoon ? '#E5E7EB' : (key === 'business' || key === 'base' ? '#F97316' : '#10B981'),
                      color: isComingSoon ? '#9CA3AF' : 'white',
                      transition: 'all 0.3s'
                    }}
                  >
                    {plan.cta}
                  </button>
                  
                  {plan.ctaSecondary && (
                    <a href="#" style={{ display: 'block', textAlign: 'center', color: '#F97316', fontSize: '0.9rem', textDecoration: 'none', fontWeight: '500', marginBottom: '1.5rem' }}>
                      {plan.ctaSecondary}
                    </a>
                  )}
                  
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {plan.features.map((feature, index) => (
                      <li key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '0.75rem', fontSize: '0.95rem', color: '#374151' }}>
                        <span style={{ color: '#10B981', fontWeight: 'bold', marginRight: '0.75rem', flexShrink: 0 }}>✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>        
      </div>

      {/* Bottom Icons */}
      <div style={{ position: 'fixed', bottom: windowWidth <= 768 ? '1rem' : '2rem', right: windowWidth <= 768 ? '1rem' : '2rem', display: 'flex', gap: '1rem', zIndex: 1000 }}>
        <div style={{ width: windowWidth <= 768 ? '44px' : '48px', height: windowWidth <= 768 ? '44px' : '48px', borderRadius: '50%', backgroundColor: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          ⚙️
        </div>
        <div style={{ width: windowWidth <= 768 ? '44px' : '48px', height: windowWidth <= 768 ? '44px' : '48px', borderRadius: '50%', backgroundColor: '#F97316', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', cursor: 'pointer', boxShadow: '0 4px 12px rgba(249, 115, 22, 0.4)' }}>
          💬
        </div>
      </div>
    </div>
  );
};

export default PricingPageComponent;